/**
 * Photo enrichment for the unit drawer's Moradores tab.
 *
 * `GET /units/:id/residents` historically returned no face for anyone, so the tab filled the gap
 * client-side from `GET /people/:id/full`, which resolves the person's live primary identity photo.
 * That fallback was gated on a single sampled resident:
 *
 *     if (people.length > 0 && !people[0].identity_photo_url && !people[0].profile_picture_url)
 *
 * which applied resident #0's verdict to the entire unit. The tab therefore worked only while
 * whoever sorted first happened to have no photo fields at all. The moment one resident gained a
 * non-empty `profile_picture_url`, enrichment switched off for everybody behind them and the tab
 * "stopped working" with no code change behind it.
 *
 * The second half of that gate was wrong on its own terms too. `people.profile_picture_url` is a
 * mirror the backend writes once — at self-registration, or when a photo request is approved — and
 * nothing repoints it when the identity photo it names is later replaced or deleted. Deleting an
 * identity photo deletes the S3 object with it, so the column is left naming a key that answers
 * 404 forever. A non-empty `profile_picture_url` is not evidence of a photo that loads; only
 * `identity_photo_url`, resolved from the live `person_identity_photos` row, is.
 */

/** The shape this module needs; resident payloads carry far more than this. */
export interface ResidentPhotoFields {
  id: string;
  identity_photo_url?: string | null;
  profile_picture_url?: string | null;
  relation_type?: string;
  parentesco?: string;
  [key: string]: any;
}

/** Resolves one person's full record, or null/throws when it cannot be had. */
export type FetchFullPerson = (personId: string) => Promise<any>;

/**
 * True when this resident still needs their photo looked up.
 *
 * Deliberately ignores `profile_picture_url`: see the module comment — it can name a deleted
 * object, and treating it as "has a photo" is what stranded residents on a permanent 404.
 */
export const needsPhotoEnrichment = (person: ResidentPhotoFields): boolean =>
  !person.identity_photo_url;

/**
 * Fill in missing faces, deciding per resident so no one person's data can switch photos off for
 * the rest of the unit.
 *
 * A resident whose lookup fails or comes back empty is returned exactly as received — they keep
 * whatever the list endpoint gave them and fall back to initials client-side.
 *
 * `relation_type` and `parentesco` are re-applied AFTER the spread, not before it. Only the
 * residents endpoint knows them, and spreading the person record on top of them silently replaced
 * both with undefined the moment that record carried the keys at all — which is the opposite of
 * the preservation this is here to do. Applied after, and only when we actually have a value, the
 * person record can never take them away.
 */
export const enrichResidentsWithPhotos = async (
  people: ResidentPhotoFields[],
  fetchFullPerson: FetchFullPerson
): Promise<ResidentPhotoFields[]> =>
  Promise.all(
    people.map(async (person) => {
      if (!needsPhotoEnrichment(person)) return person;
      try {
        const fullPerson = await fetchFullPerson(person.id);
        if (fullPerson) {
          const merged: ResidentPhotoFields = { ...fullPerson };
          if (person.relation_type !== undefined) merged.relation_type = person.relation_type;
          if (person.parentesco !== undefined) merged.parentesco = person.parentesco;
          return merged;
        }
      } catch {
        // Keep the resident we already have rather than dropping them from the list.
      }
      return person;
    })
  );
